import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { facePaint } from '../../../domain/theme/utils/facePaint';
import { theme } from '../../../domain/theme';
import { stringOrArrayPlusNull } from '../../propTypes';
import { breakpointService } from '../../../domain/services/breakpointService';
import { imageResizerService } from '../../../domain/services/imageResizerService';

export const Container = ({
  display,
  maxWidth,
  margin,
  padding,
  backgroundColor,
  backgroundImage,
  backgroundImageOpacity,
  childrenMarginBottom,
  children
}) => {
  /*
  Do not use "position: relative; z-index: 0;", because it creates a new stacking context
  @see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_positioned_layout/Understanding_z-index/Stacking_context
  '
  A stacking context is formed, anywhere in the document, by any element in the following scenarios:
  ...
  * Element with a position value absolute or relative and z-index value other than auto.
  ...
  '
  A stacking context inside containers creates problems when using
  ContentTakeover or a modal. Instead of using all available window space,
  it only takes up the space of its container.

  Think about adding a prop if the aforementioned code is required in some scenario.
  */
  const bgColorStyles = backgroundColor
    ? css`
        background-color: ${backgroundColor};
        color: ${['white', '#fff', '#f7f7f7'].includes(backgroundColor)
          ? 'black'
          : 'white'};
      `
    : null;

  const childrenMarginBottomStyles = childrenMarginBottom
    ? css`
        & > * {
          ${facePaint({
            'margin-bottom': theme.spacing(childrenMarginBottom)
          })};
        }
      `
    : null;

  const styles = {
    container: css`
      clear: both;
      width: 100%;
      ${bgColorStyles}
      ${facePaint({
        display,
        'max-width': maxWidth,
        margin: margin ? theme.spacing.parse(margin) : null,
        padding: padding ? theme.spacing.parse(padding) : null
      })};
      ${childrenMarginBottomStyles}
      & > .no-margin {
        margin-bottom: 0;
      }
    `
  };

  if (backgroundImage) {
    const currentBreakpointWidth =
      breakpointService.getCurrentBreakpoint().width;
    const mediaUrl = backgroundImage?.entity?.mediaImage?.url;
    const backgroundImageUrl = mediaUrl
      ? imageResizerService.getStyleUrl(mediaUrl, currentBreakpointWidth)
      : null;
    /*
      Do not use "position: relative; z-index: 0;" unless is required by a background image,
      because it creates a new stacking context
      @see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_positioned_layout/Understanding_z-index/Stacking_context
      '
      A stacking context is formed, anywhere in the document, by any element in the following scenarios:
      ...
      * Element with a position value absolute or relative and z-index value other than auto.
      ...
      '
      A stacking context inside containers creates problems when using
      ContentTakeover or a modal. Instead of using all available window space,
      it only takes up the space of its container.
      Think about adding a prop if the aforementioned code is required in some scenario.
    */
    styles.backgroundImage = css`
      position: relative;
      z-index: 0;
      &:before {
        background: url(${backgroundImageUrl}) no-repeat center center;
        background-size: cover;
        content: '';
        position: absolute;
        z-index: -1;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        opacity: ${backgroundImageOpacity
          ? (100 - backgroundImageOpacity) / 100
          : 1};
      }
    `;
  }

  return <div css={[styles.container, styles.backgroundImage]}>{children}</div>;
};

Container.defaultProps = {
  display: undefined,
  maxWidth: undefined,
  margin: 'auto',
  padding: undefined,
  backgroundColor: undefined,
  backgroundImage: undefined,
  backgroundImageOpacity: undefined,
  childrenMarginBottom: undefined
};

Container.propTypes = {
  display: stringOrArrayPlusNull,
  maxWidth: stringOrArrayPlusNull,
  margin: stringOrArrayPlusNull,
  padding: stringOrArrayPlusNull,
  backgroundColor: PropTypes.string,
  backgroundImage: PropTypes.any,
  backgroundImageOpacity: PropTypes.number,
  childrenMarginBottom: stringOrArrayPlusNull,
  children: PropTypes.node.isRequired
};
